import { State } from "redux/store";
import {
  unstable_useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useCreateBatchForAddMediaApiV1WorkflowDatasetDatasetIdBatchNewPost } from "vl-api/vlComponents";
import {
  fetchCVATData,
  fetchEventLogs,
  fetchNewDataset,
  resetStore,
  setFilesUploadProgress,
  setFilesUploadSize,
  setUploadingFolder,
} from "redux/CreateDataset/actions";
import {
  getActivityLogs,
  getFilesUploadProgress,
  getNewDataset,
  getUploadingFolder,
} from "redux/CreateDataset/selectors";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import {
  CVAT_INTEGRATION,
  DATASET_CREATION_V2,
  DATASET_INGESTION_ON_PREM,
  USAGE_REPORT,
} from "helpers/constants/filters";

import { PRIVATE_DATASETS_PATH } from "helpers/constants/paths";
import { UploadingFolder } from "types";
import ActivityLogErrorModal from "./Modals/ActivityLogErrorModal";
import DatasetCreationWizard from "./DatasetCreationWizard";
import OnPremDatasetCreation from "./OnPremDatasetCreation";
import QuotaLimitModal from "views/modals/QuotaLimitModal";
import StartHereBanner from "./StartHereBanner";
import DataViewSection from "./DataViewSection";
import UploadProgress from "./UploadProgress";
import ImageError from "./Modals/ImageError";
import FatalError from "./Modals/FatalError";
import StepsSection from "./StepsSection";
import ProgressPane from "./ProgressPane";
import PageHeader from "./PageHeader";

import styles from "./style.module.scss";
import UploadFailure from "./UploadFailure";
import abortControllerManager from "helpers/abortControllerManager";
import { trackNewDatasetIngestionEvent } from "helpers/utility/amplitude";
import { AMP_DATASET_INGESTION_EVENT__USER_QUIT_WHILE_UPLOADING } from "helpers/constants/amplitudeEvents";
import { useUserPermission } from "../../../hooks/useUserPermission";
import { useUserConfig } from "../../../hooks/useUserConfig";
import { App } from "antd";

const DatasetCreationPage = ({
  newDataset,
  activityLogs,
  fileUploadProgress,
  uploadingFolder,
}: DatasetCreationPageProps) => {
  const { modal } = App.useApp();
  const { isLoading: isLoadingUserConfig } = useUserConfig();
  const isCVATEnabled = useFeatureFlagService(CVAT_INTEGRATION).enabled;
  const isNewCreationEnabled =
    useFeatureFlagService(DATASET_CREATION_V2).enabled;
  const isDatasetCreationV3Enabled = useFeatureFlagService(
    DATASET_INGESTION_ON_PREM,
  ).enabled;
  const isUsageReportEnabled = useFeatureFlagService(USAGE_REPORT).enabled;
  const isUserConfigLoaded = !isLoadingUserConfig;
  const [showStartBanner, setShowStartBanner] = useState(false);
  const POLLING_INTERVAL = 3000;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { newDatasetId, batchId: urlBatchId } = useParams();

  // Batch creation logic for update mode (shared by both wizard and on-prem)
  const [batchCreated, setBatchCreated] = useState(!!urlBatchId);
  const [batchId, setBatchId] = useState<string | undefined>(urlBatchId);
  const isUpdateMode = location.pathname.includes("/update");
  const isOnPrem = location.pathname.includes("on-prem");
  const canAddMedia = useUserPermission("can_add_media");

  // Redirect viewers (no add_media permission) to the dataset exploration page
  useEffect(() => {
    if (!isLoadingUserConfig && !canAddMedia && newDataset) {
      const isDatasetCreating = newDataset.creating;
      const url = isDatasetCreating
        ? "/datasets"
        : `/dataset/${newDataset.id}/data`;
      navigate(url, { replace: true });
      if (isDatasetCreating) {
        modal.info({
          autoFocusButton: null,
          content:
            "This dataset is still being created and is not accessible yet. Access will be available once the dataset is finalized",
        });
      }
    }
  }, [isLoadingUserConfig, canAddMedia, newDataset, navigate, modal]);

  // Hook for creating new batch in update mode
  const createBatchMutation =
    useCreateBatchForAddMediaApiV1WorkflowDatasetDatasetIdBatchNewPost({
      onSuccess: (data) => {
        console.log("New batch created successfully:", data);
        const newBatchId = (data as any).batch_id;
        setBatchId(newBatchId);
        if (newBatchId && newDatasetId) {
          const mode = isUpdateMode ? "update" : "create";
          const newPath = isOnPrem
            ? `/dataset/${newDatasetId}/${mode}/${newBatchId}/on-prem`
            : `/dataset/${newDatasetId}/${mode}/${newBatchId}`;
          navigate(newPath, { replace: true });
        }
      },
      onError: (error) => {
        console.error("Failed to create batch:", error);
      },
    });

  useEffect(() => {
    if (
      isUpdateMode &&
      newDatasetId &&
      !urlBatchId &&
      !batchCreated &&
      !createBatchMutation.isPending
    ) {
      console.log("Creating new batch for update mode");
      setBatchCreated(true);
      createBatchMutation.mutate({
        pathParams: {
          datasetId: newDatasetId,
        },
      });
    }
  }, [
    isUpdateMode,
    newDatasetId,
    urlBatchId,
    batchCreated,
    createBatchMutation.isPending,
    createBatchMutation,
  ]);
  const blocker = unstable_useBlocker(
    ({ currentLocation, nextLocation }) =>
      (fileUploadProgress !== null || uploadingFolder !== null) &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (
      blocker.state === "blocked" &&
      window.confirm(
        "We're still uploading your files, sure you want to leave?",
      )
    ) {
      //track user quit event
      trackNewDatasetIngestionEvent(
        AMP_DATASET_INGESTION_EVENT__USER_QUIT_WHILE_UPLOADING,
      );

      abortControllerManager.abortController();
      blocker.proceed();
      navigate(blocker.location.pathname + blocker.location.search);
    } else {
      if (blocker.reset) {
        blocker.reset();
      }
    }
  }, [navigate, dispatch, blocker, blocker.state]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (fileUploadProgress !== null || uploadingFolder !== null) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    const handleUnload = () => {
      if (fileUploadProgress !== null) {
        dispatch(setFilesUploadProgress(null));
        dispatch(setFilesUploadSize(null));
      }
      if (uploadingFolder !== null) {
        dispatch(setUploadingFolder(null));
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [dispatch, fileUploadProgress, uploadingFolder]);

  useEffect(() => {
    if (newDatasetId && !newDataset) {
      (dispatch(fetchNewDataset(newDatasetId) as any) as Promise<any>).then(
        (result: any) => {
          if (result === "ACCESS_DENIED") {
            navigate(PRIVATE_DATASETS_PATH);
          }
        },
      );
    }
    return () => {
      dispatch(resetStore());
    };
    //eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    if (isNewCreationEnabled || isDatasetCreationV3Enabled) return;

    if (newDatasetId) {
      dispatch(fetchEventLogs(newDatasetId, true, isUsageReportEnabled) as any);
    }

    const intervalId = setInterval(() => {
      if (newDatasetId) {
        dispatch(
          fetchEventLogs(newDatasetId, false, isUsageReportEnabled) as any,
        );
      }
    }, POLLING_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    dispatch,
    newDatasetId,
    isNewCreationEnabled,
    isDatasetCreationV3Enabled,
    isUsageReportEnabled,
  ]);

  useEffect(() => {
    if (
      activityLogs.length === 1 &&
      activityLogs[0]?.event_type === "DatasetInitialized"
    ) {
      setShowStartBanner(true);
    } else {
      setShowStartBanner(false);
    }
    //eslint-disable-next-line
  }, [activityLogs.length]);

  useEffect(() => {
    if (isCVATEnabled) {
      dispatch(fetchCVATData(newDatasetId!) as any);
    }
    //eslint-disable-next-line
  }, [isCVATEnabled]);

  const renderInnerBody = () => {
    if (!isUserConfigLoaded || !newDataset || isLoadingUserConfig) {
      return null;
    }

    if (isDatasetCreationV3Enabled) {
      // On-premise dataset creation flow
      return <OnPremDatasetCreation batchId={batchId} />;
    }

    if (!isNewCreationEnabled) {
      // old implementation
      return (
        <>
          <StepsSection
            showStartBanner={showStartBanner}
            setShowStartBanner={setShowStartBanner}
            modelPack={newDataset?.model_pack}
          />
          <div className={styles.sectionDivider}>
            {showStartBanner && <StartHereBanner />}
          </div>
          <DataViewSection />

          <div className={styles.progressSection}>
            <UploadFailure />
            <UploadProgress />
            <ProgressPane />
          </div>
          <ImageError />
          <FatalError />
          <ActivityLogErrorModal />
          <QuotaLimitModal />
        </>
      );
    }

    return (
      <DatasetCreationWizard batchId={batchId} status={newDataset?.status} />
    );
  };

  return (
    <>
      <PageHeader />
      <div className={styles.innerBody}>{renderInnerBody()}</div>
    </>
  );
};

const mapStateToProps = (state: State) => {
  return {
    newDataset: getNewDataset(state),
    activityLogs: getActivityLogs(state),
    fileUploadProgress: getFilesUploadProgress(state),
    uploadingFolder: getUploadingFolder(state),
  };
};

export default connect(mapStateToProps)(DatasetCreationPage);

interface DatasetCreationPageProps {
  newDataset: any;
  activityLogs: any[];
  fileUploadProgress: number | null;
  uploadingFolder: UploadingFolder | null;
}
